import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { 
  Music, Heart, Leaf, RefreshCw, Copy, Check,
  Sparkles, Volume2, Play, Pause
} from 'lucide-react';
import { toast } from 'sonner';
import needsData from '../data/needs.json';

/**
 * NeedsSongGenerator - Create your "Belonging Song" from 3 key needs
 * 
 * From the Belonging Sessions e-book - users select 3 needs that are most
 * important for their growth in belonging, and it generates a personal song/poem.
 * 
 * Theme: Green = Growth in Belonging
 */

// Categorized needs for selection
const NEEDS_CATEGORIES = needsData?.categories || {
  connection: ['acceptance', 'belonging', 'closeness', 'communication', 'community', 'companionship', 'empathy', 'inclusion', 'intimacy', 'love', 'mutuality', 'nurturing', 'respect', 'support', 'trust', 'understanding', 'warmth'],
  physicalWellBeing: ['air', 'food', 'health', 'movement', 'rest', 'safety', 'shelter', 'touch', 'water'],
  honesty: ['authenticity', 'integrity', 'presence', 'self-connection'],
  play: ['joy', 'humor', 'fun', 'relaxation', 'rejuvenation'],
  peace: ['beauty', 'ease', 'equality', 'harmony', 'inspiration', 'order', 'peace'],
  autonomy: ['choice', 'freedom', 'independence', 'space', 'spontaneity'],
  meaning: ['awareness', 'challenge', 'clarity', 'competence', 'consciousness', 'contribution', 'creativity', 'discovery', 'effectiveness', 'growth', 'hope', 'learning', 'mourning', 'participation', 'purpose', 'self-expression', 'stimulation', 'understanding']
};

// Song templates that incorporate the 3 needs
const SONG_TEMPLATES = [
  {
    id: 'dreaming',
    title: 'The Belonging Song',
    verses: (needs) => [
      'Off balance was I dreaming,',
      'but my thoughts were clear,',
      'right then I remembered,',
      'three things we hold most dear...',
      '',
      `${needs[0]}`,
      `${needs[1]}`,
      `${needs[2]}`,
      '',
      'These are what I need to grow,',
      'in belonging, I now know.'
    ]
  },
  {
    id: 'growing',
    title: 'Seeds of Belonging',
    verses: (needs) => [
      'In the garden of my soul,',
      'three seeds I choose to grow,',
      `The seed of ${needs[0].toLowerCase()},`,
      `The seed of ${needs[1].toLowerCase()},`,
      `The seed of ${needs[2].toLowerCase()},`,
      '',
      'With water, light, and time,',
      'belonging becomes mine.'
    ]
  },
  {
    id: 'compass',
    title: 'My Compass North',
    verses: (needs) => [
      'When I lose my way,',
      'I return to what I need most:',
      '',
      `${needs[0]} guides my heart,`,
      `${needs[1]} steadies my soul,`,
      `${needs[2]} lights my path,`,
      '',
      'These three make me whole.'
    ]
  }
];

// Need chip component for selection
const NeedChip = ({ need, isSelected, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled && !isSelected}
    className={`
      px-3 py-1.5 rounded-full text-sm transition-all
      ${isSelected 
        ? 'bg-emerald-500/30 border-emerald-400 text-emerald-300 border-2' 
        : disabled
          ? 'bg-zinc-800/30 border-zinc-700/30 text-zinc-600 border cursor-not-allowed'
          : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 border hover:border-emerald-500/50 hover:text-emerald-400'
      }
    `}
    data-testid={`need-chip-${need}`}
  >
    {need}
  </button>
);

// Song Display Component
const SongDisplay = ({ template, needs, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const verses = template.verses(needs);
  
  const handleCopy = () => {
    const songText = verses.join('\n');
    navigator.clipboard.writeText(songText);
    setCopied(true);
    toast.success('Song copied');
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };
  
  return (
    <div 
      className="relative p-8 rounded-2xl bg-gradient-to-br from-emerald-900/20 to-emerald-800/10 border border-emerald-500/30"
      data-testid="song-display"
    >
      {/* Decorative elements */}
      <div className="absolute top-4 right-4 flex gap-2">
        <Leaf className="w-5 h-5 text-emerald-400/50" />
        <Heart className="w-5 h-5 text-emerald-400/50" />
        <Leaf className="w-5 h-5 text-emerald-400/50 transform scale-x-[-1]" />
      </div>
      
      <div className="text-center">
        <h3 className="text-xl font-serif text-emerald-300 mb-6">
          {template.title}
        </h3>
        
        <div className="space-y-1">
          {verses.map((line, idx) => (
            <p 
              key={idx}
              className={`
                text-lg leading-relaxed
                ${line === needs[0] || line === needs[1] || line === needs[2]
                  ? 'font-bold text-emerald-400 text-xl'
                  : line === ''
                    ? 'h-4'
                    : 'text-zinc-300 italic'
                }
              `}
              style={{ fontFamily: line === needs[0] || line === needs[1] || line === needs[2] ? 'Playfair Display, serif' : 'inherit' }}
            >
              {line}
            </p>
          ))}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="mt-6 text-emerald-400 hover:text-emerald-300"
        >
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? 'Copied' : 'Copy Song'}
        </Button>
      </div>
    </div>
  );
};

// Main Needs Song Generator Component
export const NeedsSongGenerator = ({ 
  onComplete, 
  onNeedsSelected,
  initialNeeds = [],
  showFullExperience = true 
}) => {
  const [selectedNeeds, setSelectedNeeds] = useState(initialNeeds);
  const [currentTemplate, setCurrentTemplate] = useState(0);
  const [showSong, setShowSong] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('connection');
  
  const maxNeeds = 3;
  const canSelectMore = selectedNeeds.length < maxNeeds;
  
  const toggleNeed = (need) => {
    const formattedNeed = need.charAt(0).toUpperCase() + need.slice(1);
    
    if (selectedNeeds.includes(formattedNeed)) {
      setSelectedNeeds(prev => prev.filter(n => n !== formattedNeed));
    } else if (canSelectMore) {
      setSelectedNeeds(prev => [...prev, formattedNeed]);
    }
  };
  
  const generateSong = () => {
    if (selectedNeeds.length === 3) {
      setShowSong(true);
      onNeedsSelected?.(selectedNeeds);
    }
  };
  
  const reset = () => {
    setSelectedNeeds([]);
    setShowSong(false);
  };
  
  const cycleTemplate = () => {
    setCurrentTemplate(prev => (prev + 1) % SONG_TEMPLATES.length);
  };
  
  if (!showFullExperience) {
    // Compact inline version
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <Leaf className="w-4 h-4" />
          <span>What do you need most to grow in belonging?</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedNeeds.map((need, idx) => (
            <Badge key={idx} className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
              {need}
            </Badge>
          ))}
          {selectedNeeds.length < 3 && (
            <span className="text-xs text-zinc-500">
              Select {3 - selectedNeeds.length} more...
            </span>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <Card className="bg-zinc-900/80 border-zinc-700/50 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="font-serif text-xl text-emerald-400 flex items-center gap-2">
          <Leaf className="w-5 h-5" />
          Your Belonging Song
        </CardTitle>
        <p className="text-sm text-zinc-400">
          What do you need most to grow in belonging? Select your three most essential needs.
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {!showSong ? (
          <>
            {/* Selected Needs Display */}
            <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <div className="flex gap-2">
                {[0, 1, 2].map(idx => (
                  <div 
                    key={idx}
                    className={`
                      w-32 h-10 rounded-lg border-2 flex items-center justify-center
                      ${selectedNeeds[idx] 
                        ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300' 
                        : 'border-dashed border-zinc-600 text-zinc-600'
                      }
                    `}
                  >
                    {selectedNeeds[idx] || `Need ${idx + 1}`}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {Object.keys(NEEDS_CATEGORIES).map(category => (
                <button
                  key={category}
                  onClick={() => setExpandedCategory(category)}
                  className={`
                    px-3 py-1 rounded-full text-xs font-medium transition-all
                    ${expandedCategory === category
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'bg-zinc-800/50 text-zinc-500 hover:text-zinc-300'
                    }
                  `}
                >
                  {category.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              ))}
            </div>
            
            {/* Needs Grid */}
            <div className="flex flex-wrap gap-2 p-4 rounded-lg bg-zinc-800/30 max-h-48 overflow-y-auto">
              {NEEDS_CATEGORIES[expandedCategory]?.map(need => (
                <NeedChip
                  key={need}
                  need={need}
                  isSelected={selectedNeeds.includes(need.charAt(0).toUpperCase() + need.slice(1))}
                  onClick={() => toggleNeed(need)}
                  disabled={!canSelectMore}
                />
              ))}
            </div>
            
            {/* Generate Button */}
            <div className="flex justify-center pt-4">
              <Button
                onClick={generateSong}
                disabled={selectedNeeds.length !== 3}
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-8"
                data-testid="generate-song-btn"
              >
                <Music className="w-4 h-4 mr-2" />
                Create Your Song
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Song Display */}
            <SongDisplay 
              template={SONG_TEMPLATES[currentTemplate]}
              needs={selectedNeeds}
            />
            
            {/* Controls */}
            <div className="flex items-center justify-between pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={reset}
                className="text-zinc-400 hover:text-zinc-300"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">
                  Template {currentTemplate + 1}/{SONG_TEMPLATES.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cycleTemplate}
                  className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/20"
                >
                  Try Another Style
                </Button>
              </div>
              
              {onComplete && (
                <Button
                  onClick={() => onComplete(selectedNeeds)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Continue
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Dialog version for use in the ElchmE flow
export const NeedsSongDialog = ({ open, onOpenChange, onComplete }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-zinc-900/95 backdrop-blur-xl border-zinc-700/50 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="needs-song-dialog"
      >
        <NeedsSongGenerator 
          onComplete={(needs) => {
            onComplete?.(needs);
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default NeedsSongGenerator;
